import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { AiOutlineEye } from "react-icons/ai";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import { getContractsApi, deleteContractApi } from "../../../../Api/ClientWork/ClientWork";
import ContractDetailModal from "./ContractDetailModal";

/**
 * Client Contracts Table Screen
 * Styled matching the project standard theme from ClientsTable and GatePassTable
 */
const ContractsTable = () => {
  const history = useHistory();
  const gridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    const result = await getContractsApi();
    if (result?.data?.IsSuccess === true) {
      setContracts(result.data.Data || []);
    } else {
      toast.error(result?.data?.Message || "Could not load contracts");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async (id, contractNo) => {
    if (
      window.confirm(`Are you sure you want to delete contract "${contractNo || "this contract"}"?`)
    ) {
      const result = await deleteContractApi(id);
      if (result?.data?.IsSuccess === true) {
        setContracts((prev) => prev.filter((c) => c.Id !== id));
        toast.success("Contract deleted successfully");
      } else {
        toast.error(result?.data?.Message || "Could not delete contract");
      }
    }
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Contract No",
        field: "ContractNo",
        flex: 1.3,
        minWidth: 150,
        cellRenderer: (params) => (
          <span
            style={{
              fontWeight: 600,
              color: "var(--color-primary, #0b6462)",
              cursor: "pointer",
            }}
            title="Click to view contract details"
            onClick={() => {
              setSelectedContract(params.data);
              setIsDetailModalOpen(true);
            }}
          >
            {params.value || "—"}
          </span>
        ),
      },
      {
        headerName: "Project Owner (Client)",
        field: "ClientName",
        flex: 2,
        minWidth: 200,
        tooltipValueGetter: (p) =>
          p.data?.ClientNTN ? `${p.value || ""} (NTN: ${p.data.ClientNTN})` : p.value,
      },
      {
        headerName: "Project Name",
        field: "ProjectName",
        flex: 1.6,
        minWidth: 170,
      },
      {
        headerName: "Contract Type",
        field: "ContractType",
        flex: 1.4,
        minWidth: 160,
      },
      {
        headerName: "Contract Value",
        field: "ContractValue",
        flex: 1.4,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? `PKR ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Mobilization Advance",
        field: "AdvanceDeposit",
        flex: 1.4,
        minWidth: 140,
        valueFormatter: (params) =>
          params.value ? `PKR ${Number(params.value).toLocaleString()}` : "—",
      },
      {
        headerName: "Retention %",
        field: "RetentionPercentage",
        width: 110,
        valueFormatter: (params) => (params.value != null ? `${params.value}%` : "—"),
      },
      {
        headerName: "Contract Date",
        field: "ContractDate",
        width: 120,
      },
      {
        headerName: "Target Completion",
        field: "TargetCompletionDate",
        width: 140,
      },
      {
        headerName: "Status",
        field: "Status",
        width: 120,
        cellRenderer: (params) => {
          const val = params.value || "Active";
          return <StatusChip status={val} type={statusChipType(val)} />;
        },
      },
      {
        headerName: "Action",
        field: "action",
        width: 140,
        pinned: "right",
        sortable: false,
        filter: false,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/client-work/contracts/add")}
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              height: "100%",
            }}
          >
            <AiOutlineEye
              title="View Contract Details"
              size="1.25em"
              style={{ cursor: "pointer", color: "#075e5c" }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedContract(params.data);
                setIsDetailModalOpen(true);
              }}
            />
            <FiEdit2
              title="Edit Contract"
              size="1.1em"
              style={{ cursor: "pointer", color: "#4ac8d1" }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/client-work/contracts/edit/${params.data?.Id}`);
              }}
            />
            <MdDeleteForever
              title="Delete Contract"
              size="1.3em"
              style={{ cursor: "pointer", color: "#dc3545" }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.data?.Id, params.data?.ContractNo);
              }}
            />
          </div>
        ),
      },
    ],
    [history, handleDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "—" : p.value,
      minWidth: 100,
    }),
    [],
  );

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  return (
    <div className="pt-2">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Contracts"
        >
          Contracts
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Contracts by No, Client, Project..."
        />
        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* ── Table Grid ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height2 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={contracts}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              onRowDoubleClicked={(e) => {
                setSelectedContract(e.data);
                setIsDetailModalOpen(true);
              }}
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </div>
      </div>

      {/* ── Contract Detail Modal ── */}
      {isDetailModalOpen && (
        <ContractDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedContract(null);
          }}
          contractData={selectedContract}
        />
      )}
    </div>
  );
};

export default ContractsTable;
